import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { qrLoginService, QrLoginSession, QrLoginStatus } from '../services/qrLoginService';
import { useUserStore } from '../stores/useUserStore';
import { useNotification } from '../hooks/useNotification';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, RefreshCw, Copy, Check, QrCode, Clock } from 'lucide-react';
import QRCode from 'qrcode';
import { configs } from '../configs';
import authProvider from '../authProvider';
import { StorageService } from 'neko-common';

interface QRCodeLoginDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const QRCodeLoginDialog: React.FC<QRCodeLoginDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const { fetchUser, user: currentUser } = useUserStore();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [session, setSession] = useState<QrLoginSession | null>(null);
  const [qrCodeSVG, setQrCodeSVG] = useState<string>('');
  const [status, setStatus] = useState<QrLoginStatus | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [copied, setCopied] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Generate session when dialog opens
  useEffect(() => {
    if (open && !session) {
      // Reset status to prevent stale state from triggering exchangeSession
      setStatus(null);
      generateSession();
    }

    return () => {
      // Cleanup on unmount or close
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [open]);

  // Start polling when session is created
  useEffect(() => {
    if (session && open) {
      startPolling();
      startTimer();
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [session, open]);

  // Check if confirmed and exchange token
  useEffect(() => {
    // Only run if dialog is open and we have a confirmed status
    if (open && status?.confirmed && session && !status.used) {
      exchangeSession();
    }
  }, [open, status, session]);

  const generateSession = async () => {
    try {
      setGenerating(true);
      const newSession = await qrLoginService.generateSession();
      setSession(newSession);

      // Generate QR code SVG
      // qrUrl from backend already includes full URL with verification code
      const qrUrl = newSession.qrUrl || `${window.location.origin}/qr-login/${newSession.sessionId}?code=${newSession.verificationCode}`;
      const svg = await QRCode.toString(qrUrl, {
        type: 'svg',
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF',
        },
      });
      setQrCodeSVG(svg);
      setTimeRemaining(newSession.expiresIn);
    } catch (error) {
      console.error('Failed to generate QR session:', error);
      
      // Check if it's an authentication error
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate QR code';
      const isAuthError = errorMessage.includes('401') || errorMessage.includes('Unauthorized') || errorMessage.includes('Authentication');
      
      if (isAuthError) {
      notify(
          'Your session has expired. Please refresh the page and try again.',
        { type: 'error' }
      );
      } else {
        notify(errorMessage, { type: 'error' });
      }
      
      // Don't close the dialog on auth errors - let the user try again after refreshing
      // Only close on other errors
      if (!isAuthError) {
      onClose();
      }
    } finally {
      setGenerating(false);
    }
  };

  const startPolling = () => {
    if (!session) return;

    // Poll every 2 seconds
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const newStatus = await qrLoginService.checkStatus(session.sessionId);
        setStatus(newStatus);

        if (newStatus.expired || newStatus.used) {
          // Stop polling if expired or used
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        }
      } catch (error) {
        console.error('Failed to check session status:', error);
      }
    }, 2000); // Poll every 2 seconds
  };

  const startTimer = () => {
    if (!session) return;

    timerIntervalRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          // Time expired
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const exchangeSession = async () => {
    if (!session) return;

    try {
      setLoading(true);
      const result = await qrLoginService.exchangeSession(session.sessionId);

      // Validate successful exchange: check result.success, token, and user
      if (!result.success) {
        throw new Error('Session exchange returned unsuccessful result');
      }

      if (!result.token) {
        throw new Error('No token received from session exchange');
      }

      if (!result.user || !result.user.id) {
        throw new Error('No user data received from session exchange');
      }

      // Store token using StorageService (same as AuthClient does)
      // This ensures the token is stored in the correct format that StorageService.get() expects
      const storageService = new StorageService(false); // false = localStorage (not sessionStorage)
      await storageService.set('auth.accessToken', result.token);

      // Add to recent users
      const { recentUsersService } = await import('../services/recentUsersService');
      recentUsersService.addRecentUser(result.user);

      // Stop polling
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }

      // Update user store - fetch user to ensure all state is properly refreshed
      // This validates the token works and updates the user state
      try {
        await fetchUser();
      } catch (fetchError) {
        console.error('Failed to fetch user after switch:', fetchError);
        // Even if fetchUser fails, we have the token and user data from exchange
        // The user state should still be updated via the token
        // But we'll notify about potential issues
        throw new Error('Failed to refresh user state after switch');
      }

      // Verify the user actually changed (optional validation)
      // Note: This is just for logging/debugging, not a failure condition
      if (currentUser && currentUser.id === result.user.id) {
        console.log('User switched to the same user:', result.user.id);
      }

      notify(translate('pos.auth.qr_login.success', 'Logged in successfully!'), {
        type: 'success',
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to exchange session:', error);
      notify(
        error instanceof Error ? error.message : 'Failed to complete login',
        { type: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = () => {
    if (session?.verificationCode) {
      navigator.clipboard.writeText(session.verificationCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      notify(translate('pos.auth.qr_login.code_copied', 'Code copied!'), {
        type: 'success',
      });
    }
  };

  const handleRefresh = () => {
    // Clean up existing intervals
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    // Reset state
    setSession(null);
    setQrCodeSVG('');
    setStatus(null);
    setTimeRemaining(0);

    // Generate new session
    generateSession();
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleClose = () => {
    // Clean up intervals
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    // Reset state
    setSession(null);
    setQrCodeSVG('');
    setStatus(null);
    setTimeRemaining(0);
    setCopied(false);

    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {translate('pos.auth.qr_login.switch_user', 'Switch User')}
          </DialogTitle>
          <DialogDescription>
            {translate(
              'pos.auth.qr_login.description',
              'Scan the QR code with your mobile device to log in. Share the verification code when prompted.'
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {generating || !session ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="mt-4 text-sm text-muted-foreground">
                {translate('pos.auth.qr_login.generating', 'Generating QR code...')}
              </p>
            </div>
          ) : (
            <>
              {/* QR Code */}
              <div className="flex flex-col items-center space-y-4">
                <div className="relative">
                  {qrCodeSVG && (
                    <div
                      dangerouslySetInnerHTML={{ __html: qrCodeSVG }}
                      className="rounded-lg border p-2 bg-white"
                    />
                  )}
                  {status?.confirmed && (
                    <div className="absolute inset-0 flex items-center justify-center bg-background/80 rounded-lg">
                      <div className="flex flex-col items-center space-y-2">
                        <Check className="h-8 w-8 text-green-500" />
                        <p className="text-sm font-medium text-green-500">
                          {translate('pos.auth.qr_login.confirmed', 'Confirmed!')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Timer */}
                {timeRemaining > 0 && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    <span>
                      {translate('pos.auth.qr_login.expires_in', 'Expires in')}:{' '}
                      <strong>{formatTime(timeRemaining)}</strong>
                    </span>
                  </div>
                )}

                {timeRemaining === 0 && !status?.confirmed && (
                  <p className="text-sm text-destructive">
                    {translate('pos.auth.qr_login.expired', 'QR code has expired')}
                  </p>
                )}
              </div>

              {/* Verification Code */}
              <div className="space-y-2">
                <Label>
                  {translate(
                    'pos.auth.qr_login.verification_code',
                    'Verification Code'
                  )}
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={session.verificationCode}
                    readOnly
                    className="font-mono text-lg text-center font-bold tracking-widest"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleCopyCode}
                    disabled={copied}
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {translate(
                    'pos.auth.qr_login.code_help',
                    'Share this code when confirming on your mobile device'
                  )}
                </p>
              </div>

              {/* Status Messages */}
              {status && (
                <div className="rounded-lg border p-3 space-y-2">
                  {!status.confirmed && !status.expired && (
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {translate(
                        'pos.auth.qr_login.waiting_confirmation',
                        'Waiting for confirmation...'
                      )}
                    </p>
                  )}
                  {status.expired && (
                    <p className="text-sm text-destructive">
                      {translate('pos.auth.qr_login.session_expired', 'Session expired')}
                    </p>
                  )}
                  {status.used && (
                    <p className="text-sm text-destructive">
                      {translate('pos.auth.qr_login.already_used', 'Already used')}
                    </p>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={handleRefresh}
                  disabled={generating || loading}
                  className="flex-1"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {translate('pos.auth.qr_login.refresh', 'Refresh')}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleClose}
                  disabled={loading}
                  className="flex-1"
                >
                  {translate('pos.common.action.close', 'Close')}
                </Button>
              </div>

              {/* Loading overlay */}
              {loading && (
                <div className="absolute inset-0 bg-background/80 flex items-center justify-center rounded-lg">
                  <div className="flex flex-col items-center space-y-2">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <p className="text-sm font-medium">
                      {translate('pos.auth.qr_login.logging_in', 'Logging in...')}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
